// indexedDB.js

const DB_NAME = "gennera_indexed_db";
// [Added by E2logy]: version 2 adds the survey photo queue store, version 3 adds
// the queue for GPV ratings and comments of those photos. The legacy
// "offline_uploading_images" store is kept as is so photos captured by an older
// build of the app are still uploaded after the user updates.
const DB_VERSION = 3;
const STORE_NAME = "offline_uploading_images";
export const PHOTO_QUEUE_STORE = "pending_survey_photos";
export const PORTAL_QUEUE_STORE = "pending_photo_portals";

export const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true
        });
      }
      if (!db.objectStoreNames.contains(PHOTO_QUEUE_STORE)) {
        const photoStore = db.createObjectStore(PHOTO_QUEUE_STORE, {
          keyPath: "uuid"
        });
        photoStore.createIndex("createdAt", "createdAt", { unique: false });
        photoStore.createIndex("status", "status", { unique: false });
        photoStore.createIndex(
          "worksessionPosBrandId",
          "worksessionPosBrandId",
          { unique: false }
        );
      }
      if (!db.objectStoreNames.contains(PORTAL_QUEUE_STORE)) {
        const portalStore = db.createObjectStore(PORTAL_QUEUE_STORE, {
          keyPath: "uuid"
        });
        portalStore.createIndex("createdAt", "createdAt", { unique: false });
        portalStore.createIndex(
          "worksessionPosBrandId",
          "worksessionPosBrandId",
          { unique: false }
        );
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onblocked = () => {
      console.log("indexedDB upgrade blocked by another open tab");
    };

    request.onerror = error => {
      reject(error);
    };
  });
};

export const saveImage = (imageData, formBody) => {
  return openDatabase().then(db => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const request = store.add({ data: imageData, formBody });

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = error => {
        reject(error);
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = () => db.close();
    });
  });
};

export const getAllImages = () => {
  return openDatabase().then(db => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);

      const elements = [];
      const keys = [];

      const request = store.openCursor();

      request.onsuccess = event => {
        // resolve(request.result);
        const cursor = event.target.result;

        if (cursor) {
          // Add the element and key to their respective arrays
          elements.push(cursor.value);
          keys.push(cursor.key);

          // Move to the next item in the store
          cursor.continue();
        } else {
          // No more items in the store, resolve with the elements and keys
          resolve({ elements, keys });
        }
      };

      request.onerror = error => {
        reject(error);
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = () => db.close();
    });
  });
};

export const removeImageByKey = key => {
  return openDatabase().then(db => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = error => {
        reject(error);
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = () => db.close();
    });
  });
};

/****** [Added by E2logy]: survey photo queue helpers ******/

// [Added by E2logy]: opening the database can wait forever when another tab
// blocks a version upgrade. A queue call gives up after this time instead, so
// the photo queue is never stuck on it and simply tries again later.
const QUEUE_OPEN_TIMEOUT_MS = 20 * 1000;

const openDatabaseWithTimeout = () =>
  new Promise((resolve, reject) => {
    let isTimedOut = false;
    const timerId = setTimeout(() => {
      isTimedOut = true;
      reject(new Error("indexedDB open timeout"));
    }, QUEUE_OPEN_TIMEOUT_MS);
    openDatabase().then(
      db => {
        clearTimeout(timerId);
        // Opened too late: the caller has given up, so do not leak the handle.
        if (isTimedOut) {
          db.close();
          return;
        }
        resolve(db);
      },
      error => {
        clearTimeout(timerId);
        reject(error);
      }
    );
  });

const runStoreTransaction = (storeName, mode, worker) => {
  return openDatabaseWithTimeout().then(db => {
    return new Promise((resolve, reject) => {
      let result;
      const transaction = db.transaction([storeName], mode);
      const store = transaction.objectStore(storeName);

      try {
        worker(store, value => {
          result = value;
        });
      } catch (error) {
        db.close();
        reject(error);
        return;
      }

      transaction.oncomplete = () => {
        db.close();
        resolve(result);
      };
      transaction.onerror = event => {
        db.close();
        reject(event);
      };
      transaction.onabort = event => {
        db.close();
        reject(event);
      };
    });
  });
};

const putRecord = (storeName, record) =>
  runStoreTransaction(storeName, "readwrite", store => {
    store.put(record);
  });

const getRecord = (storeName, uuid) =>
  runStoreTransaction(storeName, "readonly", (store, setResult) => {
    const request = store.get(uuid);
    request.onsuccess = () => setResult(request.result || null);
  });

// Oldest record first, so items reach the server in the order they were created.
const getAllRecords = storeName =>
  runStoreTransaction(storeName, "readonly", (store, setResult) => {
    const records = [];
    const request = store.openCursor();
    request.onsuccess = event => {
      const cursor = event.target.result;
      if (cursor) {
        records.push(cursor.value);
        cursor.continue();
      } else {
        records.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setResult(records);
      }
    };
  });

const removeRecord = (storeName, uuid) =>
  runStoreTransaction(storeName, "readwrite", store => {
    store.delete(uuid);
  });

const countRecords = storeName =>
  runStoreTransaction(storeName, "readonly", (store, setResult) => {
    const request = store.count();
    request.onsuccess = () => setResult(request.result || 0);
  });

// Insert or replace a queued photo. The record is keyed by its own uuid so the
// same photo can never be stored twice.
export const putPendingPhoto = record => putRecord(PHOTO_QUEUE_STORE, record);

export const getPendingPhoto = uuid => getRecord(PHOTO_QUEUE_STORE, uuid);

export const getAllPendingPhotos = () => getAllRecords(PHOTO_QUEUE_STORE);

export const removePendingPhoto = uuid =>
  removeRecord(PHOTO_QUEUE_STORE, uuid);

export const countPendingPhotos = () => countRecords(PHOTO_QUEUE_STORE);

/****** [Added by E2logy]: queue for the GPV rating and comments of a photo ******/

export const putPendingPortal = record => putRecord(PORTAL_QUEUE_STORE, record);

export const getPendingPortal = uuid => getRecord(PORTAL_QUEUE_STORE, uuid);

export const getAllPendingPortals = () => getAllRecords(PORTAL_QUEUE_STORE);

export const removePendingPortal = uuid =>
  removeRecord(PORTAL_QUEUE_STORE, uuid);

export const countPendingPortals = () => countRecords(PORTAL_QUEUE_STORE);
